import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  Route,
  Routes,
  NavLink,
  Navigate,
} from 'react-router-dom';
import { QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  AlertTriangle,
  AlertCircle,
  Camera,
  Settings,
  Plug,
  CreditCard,
  LogOut,
} from 'lucide-react';
import { AppShell } from '@avagency/frontend/components/shell/AppShell';
import { Button } from '@avagency/frontend/components/ui/button';
import { Skeleton } from '@avagency/frontend/components/ui/skeleton';
import { Toaster } from '@avagency/frontend/components/ui/sonner';
import { createQueryClient } from '@avagency/frontend/lib/api';

import { Dashboard } from './pages/Dashboard';
import { Snapshots } from './pages/Snapshots';
import { Thresholds } from './pages/Thresholds';
import { Connect } from './pages/Connect';
import { Billing } from './pages/Billing';
import { SignIn } from './pages/SignIn';
import {
  AUTH_INVALIDATE_EVENT,
  clearOAuthCallbackStatusFromURL,
  fetchIntegrations,
  fetchMe,
  parseOAuthCallbackStatus,
  signOut,
  type IntegrationListResponse,
  type Me,
  type OAuthCallbackStatus,
} from './api';
import './index.css';

const queryClient = createQueryClient();

// useMe — strict freshness auth gate (staleTime:0, refetchOnWindowFocus).
function useMe() {
  return useQuery<Me, Error & { status?: number }>({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    retry: (failureCount, err) => {
      if (err.status === 401) return false;
      return failureCount < 1;
    },
  });
}

function useIntegrations() {
  return useQuery<IntegrationListResponse, Error & { status?: number }>({
    queryKey: ['pipeline-rot', 'integrations'],
    queryFn: fetchIntegrations,
  });
}

function pickPrimaryActive(integrations: IntegrationListResponse | undefined) {
  return integrations?.integrations.find((i) => i.status === 'active');
}

function pickReauthRequired(integrations: IntegrationListResponse | undefined) {
  return integrations?.integrations.find((i) => i.status === 'reauth_required');
}

// App — auth gate: routes between SignIn and authenticated shell.
function App() {
  const me = useMe();
  const qc = useQueryClient();

  useEffect(() => {
    const handler = () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    };
    window.addEventListener(AUTH_INVALIDATE_EVENT, handler);
    return () => window.removeEventListener(AUTH_INVALIDATE_EVENT, handler);
  }, [qc]);

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8" aria-busy="true">
        <Skeleton className="h-32 w-full max-w-md" />
      </div>
    );
  }
  if (me.isError) {
    if (me.error.status === 401) {
      return <SignIn returnTo={window.location.pathname + window.location.search} />;
    }
    return <SignIn error={`Auth check failed: ${me.error.message}`} />;
  }
  return <AuthenticatedApp session={me.data!} />;
}

// AuthenticatedApp — checks integrations and routes to Connect or the dashboard shell.
function AuthenticatedApp({ session }: { session: Me }) {
  const integrations = useIntegrations();

  const [oauthStatus] = useState<{ status: OAuthCallbackStatus; error: string | null }>(() =>
    parseOAuthCallbackStatus()
  );

  useEffect(() => {
    if (oauthStatus.status !== null) clearOAuthCallbackStatusFromURL();
  }, [oauthStatus.status]);

  if (integrations.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8" aria-busy="true">
        <Skeleton className="h-48 w-full max-w-lg" />
      </div>
    );
  }

  // Error gate: don't silently treat a 500/403/401 as "no integration".
  // Show retry UI so connected tenants aren't routed to Connect by mistake.
  if (integrations.isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8">
        <div className="rounded-lg border border-destructive/30 bg-destructive/[0.08] px-6 py-4 text-sm text-destructive max-w-md w-full flex items-center gap-2">
          <AlertCircle size={16} />
          Failed to load your integrations. {(integrations.error as Error).message}
        </div>
        <button
          onClick={() => integrations.refetch()}
          className="text-sm text-primary underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const active = pickPrimaryActive(integrations.data);
  const reauthRequired = pickReauthRequired(integrations.data);

  if (!active) {
    return (
      <Connect
        session={session}
        callbackStatus={oauthStatus.status}
        callbackError={oauthStatus.error}
        reauthRequired={!!reauthRequired}
      />
    );
  }

  return <ConnectedShell session={session} integrationID={active.id} />;
}

// ConnectedShell — the full manager dashboard once an integration is active.
function ConnectedShell({ session, integrationID }: { session: Me; integrationID: string }) {
  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/api/v1/auth/signin';
  };

  const nav = [
    { to: '/', label: 'Dashboard', icon: <LayoutDashboard size={16} /> },
    { to: '/deals', label: 'Deals', icon: <AlertTriangle size={16} /> },
    { to: '/snapshots', label: 'Snapshots', icon: <Camera size={16} /> },
    { to: '/thresholds', label: 'Thresholds', icon: <Settings size={16} /> },
    { to: '/connect', label: 'CRM', icon: <Plug size={16} /> },
    { to: '/billing', label: 'Billing', icon: <CreditCard size={16} /> },
  ];

  return (
    <AppShell
      topbar={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="pr-brand-orb" aria-hidden="true" />
            <span className="text-sm font-semibold text-foreground/90">Pipeline Rot Alerter</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-muted-foreground">{session.email}</span>
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="gap-1.5">
              <LogOut size={14} />
              Sign out
            </Button>
          </div>
        </div>
      }
      sidebar={
        <nav className="flex flex-col gap-1" aria-label="Main navigation">
          {nav.map(({ to, label, icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`
              }
            >
              {icon}
              {label}
            </NavLink>
          ))}
        </nav>
      }
    >
      <Toaster />
      <Routes>
        <Route path="/" element={<Dashboard integrationID={integrationID} />} />
        <Route path="/deals" element={<Dashboard integrationID={integrationID} initialTab="deals" />} />
        <Route path="/snapshots" element={<Snapshots integrationID={integrationID} />} />
        <Route path="/thresholds" element={<Thresholds integrationID={integrationID} />} />
        <Route path="/connect" element={<Connect session={session} />} />
        <Route path="/billing" element={<Billing sessionEmail={session.email} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
